import { Component, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { FileUploadConfig, SelectedSubproject } from '@shared/models';
import {
  AppFacade,
  OfflineFacade,
  UserDetailsFacade,
} from '@shared/state/facades';
import { Constants, getStaticMessage } from '@shared/utils';
import {
  RefreshType,
  Rights,
  StaticMessage,
  dynamicTextPlaceholder,
} from '@shared/enums';
import { GridApi } from '@grid/models';
import { DprAttachmentsGridData } from '@shared/models/grid';
import { DprDetails, DprDetailsSync } from '@shared/models/dpr';
import {
  DprDetailsService,
  SaveErrorHandlerService,
} from '@dpr-details/services';
import {
  DataRefreshService,
  ScreenSizeService,
  UnsavedChangesService,
} from '@shared/services';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import { DprAppendixGridSyncResult } from '@dpr-details/models/sync';
import {
  AutoSaveableComponent,
  DeactivationGuardedComponent,
  RefreshableComponent,
} from '@shared/models/component';
import { SyncService } from '@shared/services/grid';
import { DprAppendixGridService } from '@dpr-details/services/grid';
import { DprAttachmentService } from '@shared/services/dpr';

@UntilDestroy()
@Component({
  selector: 'app-dpr-appendix-page',
  templateUrl: './dpr-appendix-page.component.html',
  styleUrls: ['./dpr-appendix-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprAppendixPageComponent
  extends DeactivationGuardedComponent
  implements OnInit, AutoSaveableComponent, RefreshableComponent
{
  userDetailsQuery = this.userDetailsFacade.query;

  dprDetails$!: Observable<DprDetails>;
  gridData$ = new BehaviorSubject<DprAttachmentsGridData[]>([]);
  isAppOnline$ = this.appFacade.query.isAppOnline$;
  clientId$ = this.userDetailsQuery.selectedClientId$;
  isDirty$ = new BehaviorSubject<boolean>(false);
  selectedSubproject$ = this.userDetailsQuery.selectedSubproject$;
  loading$ = new BehaviorSubject<boolean>(true);

  isSaving!: boolean;
  canEdit!: boolean;
  dprDetails!: DprDetails;
  selectedSubproject!: SelectedSubproject;
  dprDetailsOfflineChanges!: DprDetailsSync;
  gridApi!: GridApi<DprAttachmentsGridData>;
  fileUploadConfig: FileUploadConfig = {
    ...Constants.AttachmentFileUploadConfig,
    sizeLimit: 10 * 1024 * 1024,
    sizeLimitMessage: getStaticMessage(StaticMessage.MaxLimitMessage)
      .replace(dynamicTextPlaceholder, '10')
      .replace(dynamicTextPlaceholder, 'MB'),
    accept: '.pdf',
  };
  appendixTitle = $localize`:@@dprDetails.dprAppendixPage.titleAppendix:Appendix`;

  private gridReady$ = new BehaviorSubject<boolean>(false);
  private dprDetailsOfflineChanges$!: Observable<DprDetailsSync>;
  private gridOfflineChangesReady$ = new BehaviorSubject<boolean>(false);

  constructor(
    private activatedRoute: ActivatedRoute,
    private appFacade: AppFacade,
    private userDetailsFacade: UserDetailsFacade,
    private offlineFacade: OfflineFacade,
    private dprAttachmentService: DprAttachmentService,
    private unsavedChangesService: UnsavedChangesService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    private dprDetailsService: DprDetailsService,
    private dataRefreshService: DataRefreshService,
    private syncService: SyncService,
    private dprAppendixGridService: DprAppendixGridService,
    private saveErrorHandlerService: SaveErrorHandlerService,
    protected screenSizeService: ScreenSizeService
  ) {
    super();

    this.unsavedChangesService.register(this.save.bind(this), this.isDirty$);
  }

  //#region Lifecycle hooks

  ngOnInit(): void {
    this.initializeSelectedSubproject();
    this.initializeDprDetails();
    this.initializeDprDetailsOfflineChanges();
    this.initializeCanEdit();

    this.dataRefreshService
      .getRefreshNeeded()
      .pipe(
        untilDestroyed(this),
        filter((refreshType) => refreshType === RefreshType.Data),
        tap(() => {
          this.refreshData();
        })
      )
      .subscribe();

    combineLatest([this.dprDetailsOfflineChanges$, this.gridReady$])
      .pipe(
        untilDestroyed(this),
        filter(
          ([dprDetailsOfflineChanges, gridReady]) =>
            !!dprDetailsOfflineChanges && gridReady
        ),
        tap(() => {
          // timeout is needed to execute this after the grid changes are cleared following a successful save
          setTimeout(() => {
            this.applyPredefinedChanges();
            this.setupData();
            this.gridOfflineChangesReady$.next(true);
          });
        })
      )
      .subscribe();

    combineLatest([
      this.gridOfflineChangesReady$,
      this.selectedSubproject$,
      this.dprDetails$,
    ])
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.setupData();
        })
      )
      .subscribe();

    this.activatedRoute.parent.params
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap(() => {
          this.gridApi?.reset();
          this.isDirty$.next(false);
        })
      )
      .subscribe();
  }

  //#endregion

  //#region AutoSaveableComponent

  autoSave(): void {
    if (this.canEdit && this.isDirty$.value) {
      this.save(null, true);
    }
  }

  autoSaveFromAdminChange(): Observable<boolean> {
    if (this.canEdit && this.isDirty$.value) {
      return this.save(null, false, true);
    }

    return of(false);
  }

  //#endregion

  //#region Refresh data

  refreshData(): void {
    this.gridApi.reset();
    this.setupData();
    this.isDirty$.next(false);
  }

  //#endregion

  //#region Deactivation guard

  canDeactivate(): boolean | Observable<boolean> {
    return !this.isDirty$.value;
  }

  //#endregion

  onGridRegistered(gridApi: GridApi<DprAttachmentsGridData>): void {
    this.gridApi = gridApi;

    gridApi.isDirty$
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.isDirty$.next(true);
        })
      )
      .subscribe();

    this.gridReady$.next(true);
  }

  save(
    _?,
    isAutoSync = false,
    isAutoSyncFromAdminChange = false
  ): Observable<boolean> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;

    const validateChanges$ =
      isAutoSync || isAutoSyncFromAdminChange
        ? of(true)
        : this.validateChanges();

    const saveChanges$ = validateChanges$.pipe(
      switchMap((valid) => {
        if (!valid) {
          return of(false);
        }

        return this.saveChanges(isAutoSyncFromAdminChange);
      }),
      finalize(() => {
        this.isSaving = false;
      }),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  //#region Private methods

  private validateChanges(): Observable<boolean> {
    const changes = this.gridApi.getChanges();

    if (!this.dprAttachmentService.validateChanges(changes)) {
      return of(false);
    }

    return of(true);
  }

  private saveChanges(
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprAppendixGridSyncResult> {
    const changes = this.gridApi.getChanges();

    const syncDprAppendixAttachments =
      this.dprAppendixGridService.mapToGridSync(changes);

    const syncData = this.dprDetailsSyncModelBuilderService.forDprAppendix(
      this.selectedSubproject.id,
      this.dprDetails.id,
      syncDprAppendixAttachments
    );

    const saveChanges$ = this.dprDetailsService
      .syncDprAppendix(syncData, isAutoSyncFromAdminChange)
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.onSaveSuccess(result);
      },
      error: (error: HttpErrorResponse) => {
        this.saveErrorHandlerService.onSaveError(
          this.selectedSubproject.data.subprojectConfiguration.customNames
            .appendixTabName,
          this.selectedSubproject.id,
          error
        );
      },
    });

    return saveChanges$;
  }

  private onSaveSuccess(result: DprAppendixGridSyncResult): void {
    this.refreshData();

    this.offlineFacade.setDprAppendix(
      result.dprAppendixAttachments,
      result.firstWarning,
      result.warningStatusTab,
      result.dprDetailId
    );
  }

  private initializeSelectedSubproject(): void {
    this.selectedSubproject$
      .pipe(
        filter((selectedSubproject) => selectedSubproject?.data != null),
        tap((selectedSubproject) => {
          this.selectedSubproject = selectedSubproject;

          const appendixTabName =
            selectedSubproject.data.subprojectConfiguration.customNames
              .appendixTabName;
          if (appendixTabName) {
            this.appendixTitle = appendixTabName;
          }
        })
      )
      .subscribe();
  }

  private initializeDprDetails(): void {
    this.dprDetails$ = this.activatedRoute.parent.params.pipe(
      switchMap((params) =>
        this.offlineFacade.getDprDetails(params.dprDetailId)
      ),
      filter((dprDetails) => !!dprDetails),
      tap((dprDetails) => {
        this.dprDetails = dprDetails;
        this.loading$.next(false);
      }),
      shareReplay()
    );
  }

  private initializeDprDetailsOfflineChanges(): void {
    this.dprDetailsOfflineChanges$ = combineLatest([
      this.activatedRoute.parent.params,
      this.offlineFacade.query.dprDetailsOfflineChanges$,
    ]).pipe(
      switchMap(([params, dprDetailsOfflineChanges]) =>
        of(
          // double equal needed, because in offline the ids are sent as strings
          // eslint-disable-next-line eqeqeq
          dprDetailsOfflineChanges?.find((x) => x.id == params.dprDetailId)
        )
      ),
      tap((dprDetailsOfflineChanges) => {
        this.dprDetailsOfflineChanges = dprDetailsOfflineChanges;
      })
    );
  }

  private initializeCanEdit(): void {
    combineLatest([this.selectedSubproject$, this.dprDetails$])
      .pipe(
        untilDestroyed(this),
        map(([selectedSubproject, dprDetails]) => {
          this.canEdit =
            selectedSubproject.dprScreen === Rights.Edit &&
            !selectedSubproject.isLocked &&
            !dprDetails.lockedDate;
        })
      )
      .subscribe();
  }

  private setupData(): void {
    this.dprDetails$
      .pipe(
        untilDestroyed(this),
        map((dprDetails) =>
          this.gridData$.next(
            this.dprAttachmentService.mapToGridData(
              dprDetails.dprAppendixAttachments
            )
          )
        )
      )
      .subscribe();
  }

  private applyPredefinedChanges(): void {
    const offlineChanges =
      this.dprDetailsOfflineChanges?.dprSummary?.syncDprSummaryAttachments ??
      this.syncService.getDefaultSyncModel();

    this.gridApi.setPredefinedChanges(offlineChanges);
  }

  //#endregion
}
